import { TonePicker, Toggle, Why } from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useT } from '../lib/i18n.jsx';

/**
 * What to expect at the tone you just picked.
 *
 * Until now the cost of a dark tone was disclosed only after it had been paid:
 * you chose, held still for fifty seconds, and then learned the signal was
 * never going to be strong enough. Saying it at the moment of choosing changes
 * the shape of a refusal from a surprise into the thing you were told would
 * probably happen.
 *
 * Deliberately qualitative. Every performance figure this product holds comes
 * from simulation, and an app whose entire argument is against reporting
 * numbers it has not earned should not put an unearned number on its first
 * screen. The bands become percentages when the validation study produces
 * real ones, and not before.
 */
const EXPECTATION = [
  { upTo: 3, key: 'tone.expect.strong', kind: 'ok' },
  { upTo: 6, key: 'tone.expect.good', kind: 'ok' },
  { upTo: 8, key: 'tone.expect.weak', kind: 'warn' },
  { upTo: 10, key: 'tone.expect.veryWeak', kind: 'warn' },
];

const expectationFor = (tone) => EXPECTATION.find((band) => tone <= band.upTo) ?? null;

export default function Start({ record, onTone, onConsent, onGo, consentBusy }) {
  const t = useT();

  const steps = [1, 2, 3, 4];
  const expectation = record.monkTone ? expectationFor(record.monkTone) : null;

  return (
    <section>
      <div className="vh">
        <p className="eyebrow">{t('start.eyebrow')}</p>
        <h1>
          {t('start.title.line1')}
          <br />
          <em>{t('start.title.line2')}</em>
        </h1>
      </div>

      <div className="plainwrap">
        <div className="plain">
          <p>{t('start.plain1')}</p>
          <p>{t('start.plain2')}</p>
          <p>
            {t('start.plain3')} <b>{t('start.plain3b')}</b>
          </p>
        </div>

        <div className="card">
          <p className="eyebrow">{t('start.problem')}</p>
          <div className="steps" style={{ marginTop: 'var(--s2)' }}>
            {steps.map((n) => (
              <div className="step" key={n}>
                <span className="num" />
                <p>
                  <b>{t(`start.step${n}.lead`)}</b> {t(`start.step${n}.body`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 'var(--s3)' }}>
        <h2>{t('start.before.title')}</h2>
        <p style={{ color: 'var(--ink-soft)', maxWidth: '62ch' }}>{t('start.before.body')}</p>
        <TonePicker value={record.monkTone} onChange={onTone} />

        {expectation ? (
          <p className={`expect ${expectation.kind}`} role="status" aria-live="polite">
            {t(expectation.key)}
          </p>
        ) : null}

        <div style={{ borderTop: '1px solid var(--rule)', marginTop: 'var(--s2)', paddingTop: '0.25rem' }}>
          <Toggle
            id="pledge"
            checked={record.consent.corpus}
            // Matches the consent screen, which has always disabled these. This
            // one did not, so sharing could be switched on against a service
            // that does not exist — and the state that made erasure impossible
            // was only reachable through here.
            disabled={consentBusy || !api.configured}
            onChange={(next) => onConsent('corpus', next)}
            title={t('start.pledge.title')}
            description={t('start.pledge.body')}
            earn={t('start.pledge.earn')}
          />
        </div>

        <div className="btnrow" style={{ marginTop: 'var(--s3)' }}>
          <button type="button" className="btn" onClick={() => onGo('scan')} disabled={!record.monkTone}>
            {record.monkTone ? t('start.cta.ready') : t('start.cta.needTone')}
          </button>
          <button type="button" className="btn ghost" onClick={() => onGo('pledge')}>
            {t('start.cta.counter')}
          </button>
        </div>
      </div>

      <Why>
        <strong>The first screen of almost every health app asks your age and your weight.</strong> Ori asks your skin
        tone, because that is the variable that decides whether the measurement works. Every competitor discards
        exactly the thing that makes their model fail — which is what leaves the data asset unclaimed.
      </Why>
    </section>
  );
}
